import EmObject, { computed, action } from '@ember/object';
import { isBlank, isEmpty, isPresent } from '@ember/utils';
import { IdentifierKey, ObjectIdentifierKey } from 'cio/utils/identifiers';
import hasInvalidCharacters from 'cio/utils/validators/invalid-chars';

export class AttributeProxy extends EmObject {
  key!: string;
  value!: string;
  requiredAttributes!: string[];
  permanentAttributes?: string[];
  autoGeneratedAttributes!: string[];
  isNew!: boolean;

  serverError: string | null = null;
  isCreated = false;

  _originalKey: string | null = null;
  _originalValue: string | null = null;

  init() {
    super.init();

    let { key, value } = this;

    this.setProperties({
      _originalKey: key,
      _originalValue: value,
      isCreated: key === '',
    });
  }

  @computed('requiredAttributes', 'isNew', 'key', 'value', 'isKeyChanged', 'serverError')
  get errors() {
    let errors: Record<string, string[]> = {};
    let { key, value, requiredAttributes } = this;

    // In multi identifier workspaces, users don't have to populate ALL identifiers, just one.
    if (requiredAttributes.length === 1 && requiredAttributes.includes(key) && isBlank(value)) {
      errors.value = ["Attribute value can't be blank"];
    }

    if (isBlank(key) && isPresent(value)) {
      errors.key = ["Attribute name can't be blank"];
    }

    if (hasInvalidCharacters(key)) {
      errors.key = ['Attribute name contains invalid characters'];
    }

    if (this.isNew && this.isKeyChanged && this.autoGeneratedAttributes.includes(key.trim())) {
      errors.key = [`${key.trim()} is auto-generated in this workspace`];
    } else if (
      this.isKeyChanged &&
      ((this.permanentAttributes?.includes(key.trim()) ?? false) ||
        this.requiredAttributes.includes(key.trim()) ||
        this.autoGeneratedAttributes.includes(key.trim()))
    ) {
      errors.key = ['Attribute name is already used'];
    }

    if (this.serverError) {
      errors.value = [this.serverError];
    }

    return errors;
  }

  @computed('errors')
  get isInvalid() {
    let { errors } = this;

    return Object.keys(errors).length > 0;
  }

  @computed('_originalKey', 'key')
  get isKeyChanged() {
    return this.key !== this._originalKey;
  }

  @computed('_originalValue', 'value')
  get isValueChanged() {
    return this.value !== this._originalValue;
  }

  @action
  handleValueChange() {
    this.set('serverError', null);
  }
}

const mapAttrsToProxies = (
  attrs: Record<string, any>[],
  requiredAttributes: (IdentifierKey | ObjectIdentifierKey)[],
  isNew: boolean,
  autoGeneratedAttributes: (IdentifierKey | '_created_in_customerio_at' | ObjectIdentifierKey)[],
  permanentAttributes?: string[],
) =>
  attrs.map((attr) => {
    let [key] = Object.keys(attr);
    return AttributeProxy.create({
      key,
      value: attr[key],
      requiredAttributes,
      permanentAttributes,
      autoGeneratedAttributes,
      isNew,
    });
  });

const mapProxiesToAttrs = (proxies: AttributeProxy[]) => {
  let attrs: Record<string, any> = {};

  proxies.forEach(({ key, value }) => {
    if (key !== '') {
      attrs[key] = value;
    }
  });

  return attrs;
};

const copyProxyArray = (array: AttributeProxy[]) =>
  array.map((proxy) =>
    AttributeProxy.create(
      proxy.getProperties(
        'key',
        'value',
        'requiredAttributes',
        'isNew',
        'permanentAttributes',
        'autoGeneratedAttributes',
      ),
    ),
  );

export default class AttributesProxy extends EmObject {
  // passed properties
  attrs!: Record<string, any>[];
  requiredAttributes!: (IdentifierKey | ObjectIdentifierKey)[];
  permanentAttributes?: string[];
  autoGeneratedAttributes!: (IdentifierKey | '_created_in_customerio_at' | ObjectIdentifierKey)[];
  isNew!: boolean;

  // computed properties
  currentAttrs!: AttributeProxy[];
  // hiddenAttrs is used to remove attributes.
  // They are set to empty strings on serialization.
  // See: serializers/customer.js
  hiddenAttrs!: string[];
  _originalAttrs!: AttributeProxy[];

  @computed('_originalAttrs', 'currentAttrs.@each.{key,value}')
  get isDirty() {
    return JSON.stringify(this._originalAttrs) !== JSON.stringify(this.currentAttrs);
  }

  @computed('currentAttrs.@each.isInvalid')
  get isInvalid() {
    return this.currentAttrs.some((attr) => attr.isInvalid);
  }

  init() {
    super.init();
    let attrsProxies = mapAttrsToProxies(
      this.attrs,
      this.requiredAttributes,
      this.isNew,
      this.autoGeneratedAttributes,
      this.permanentAttributes,
    );
    this.set('_originalAttrs', attrsProxies);
    this.set('currentAttrs', copyProxyArray(attrsProxies));
    this.set('hiddenAttrs', []);
  }

  @action
  getModelAttributes() {
    let { currentAttrs, hiddenAttrs } = this;

    return {
      hiddenAttrs,
      attrs: mapProxiesToAttrs(currentAttrs),
    };
  }

  @action
  add(key: string) {
    if (this.hiddenAttrs.includes(key)) {
      this.hiddenAttrs.removeObject(key);
    }

    this.create({ key, value: '' });
  }

  @action
  create({ key = '', value = '' } = {}) {
    this.currentAttrs.pushObject(
      AttributeProxy.create({
        key,
        value,
        requiredAttributes: this.requiredAttributes,
        permanentAttributes: this.permanentAttributes,
        autoGeneratedAttributes: this.autoGeneratedAttributes,
        isNew: this.isNew,
      }),
    );
  }

  @action
  remove(attribute: AttributeProxy) {
    this.hiddenAttrs.pushObject(attribute.key);
    this.currentAttrs.removeObject(attribute);
  }

  @action
  hide(key: string) {
    this.hiddenAttrs.pushObject(key);
  }

  @action
  unhide(key: string) {
    this.hiddenAttrs.removeObject(key);
  }

  @action
  reset() {
    this.set('currentAttrs', copyProxyArray(this._originalAttrs));
  }

  @action
  findAttrByKey(key: string) {
    return this.currentAttrs.findBy('key', key);
  }

  @action
  hideAttribute(attribute: Record<string, any>) {
    let { key, _originalKey } = attribute;

    // If the new name is hidden for some reason (for example, a user edited
    // a key to something else, and created a new attribute with the previous
    // key), we want to make sure it isn't hidden anymore.

    this.unhide(key);

    // If a key is edited to something else, hide the original attribute so
    // it can be removed on serialization, based on two conditions:
    //
    // (a) the original isn't empty - if it is, it's a new attribute and
    // there is no original to hide.
    //
    // (b) the original has not been edited yet. This could be the case when
    // swapping keys between two attributes.

    let attributeWithOriginalKey = this.findAttrByKey(_originalKey);

    if (!isEmpty(_originalKey) && !attributeWithOriginalKey?.isKeyChanged) {
      this.hide(_originalKey);
    }
  }
}
